import { LegacyRef, ReactNode } from "react";

import { Collapse } from "antd";
import { clsx } from "clsx";

import { RVIcon, RVIconSizeOptions } from "@regal-voice/ui";

import { DrawerTitle } from "./DrawerTitle";

import styles from "./DrawerPanel.module.scss";

type DrawerPanelBackground = "transparent" | "fill";

export function DrawerPanel({
    title,
    titleEditable,
    name,
    section,
    defaultExpanded,
    background,
    leftSideIcon,
    rightSideIcon,
    dragHandleRef,
    children,
    collapsible = true,
}: {
    title: string;
    titleEditable?: boolean;
    name?: string | number | Array<string | number>;
    section?: string | number;
    defaultExpanded?: boolean;
    background?: DrawerPanelBackground;
    leftSideIcon?: ReactNode;
    rightSideIcon?: ReactNode;
    dragHandleRef?: LegacyRef<HTMLDivElement>;
    children?: ReactNode;
    collapsible?: boolean;
}): JSX.Element {
    const draggable = !!dragHandleRef;

    // assign a class name based on the background type, if specified
    // const customStyles = !!background ? [styles[background]] : undefined;
    const customStyles = [!!background ? styles[background] : undefined, draggable && "draggable"];

    // there are 3 visual styles: default, transparent and fill
    // no need to customize the default style, but we need extra work
    // for the other two
    let ghost = false;
    let bordered = true;

    if (background === "transparent") {
        ghost = true;
        bordered = false;
    }
    if (background === "fill") {
        ghost = true;
    }

    const titleElem = (
        <DrawerTitle
            editable={titleEditable}
            title={title}
            name={name}
            leftSideIcon={leftSideIcon}
            rightSideIcon={rightSideIcon}
            dragHandleRef={dragHandleRef}
        />
    );

    return (
        <div data-testid={`drawer-panel-${title}`} className="drawer-panel">
            <Collapse
                ghost={ghost}
                bordered={bordered}
                className={clsx(styles.drawerPanel, customStyles)}
                expandIconPosition="end"
                expandIcon={({ isActive }) => {
                    const icon: string = isActive ? "arrow-down" : "arrow-right";
                    return <RVIcon className={styles.arrow} icon={icon} size={RVIconSizeOptions.XSMALL} />;
                }}
                defaultActiveKey={defaultExpanded || !collapsible ? section || title : undefined}
                collapsible={collapsible ? undefined : "disabled"}
            >
                <Collapse.Panel showArrow={collapsible} header={titleElem} key={section || title}>
                    {children}
                </Collapse.Panel>
            </Collapse>
        </div>
    );
}
